import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import './quotationPreview.css';

const STORAGE_KEY = 'quotation-preview-payload';

function Letterhead() {
    return (
        <div className="letterhead">
            <img src="/templates/images/quotation-header.png" alt="Astern Technologies" className="letterhead-img" />
        </div>
    );
}

const fmt = n => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function AttnBlock({ p }) {
    return (
        <div className="attn-grid">
            <div className="label">Attn</div><div>{p.name || ''}</div>
            <div className="label">Date</div><div>{p.date || ''}</div>
            <div className="label">Company</div><div>{p.company || ''}</div>
            <div className="label">Ref No.</div><div>{p.refNo || ''}</div>
            <div className="label">Address</div><div>{p.address || ''}</div>
            <div className="label">Tel No.</div><div>{p.phone || ''}</div>
            <div className="label">Validity</div><div>30 days</div>
            <div className="label">Email</div><div>{p.email || ''}</div>
        </div>
    );
}

function ScopeOfWork({ p }) {
    const programLine = p.program === 'SELCO' || p.program === 'SELCO/ATAP' ? 'SELCO / ATAP' : (p.program || 'ATAP');
    const kwp = Number(p.kwp || 0).toFixed(2);
    const panelQty = Number(p.panelQty || 0);
    const panelWatt = p.panelWatt || '640';
    const kwac = Number(p.kwac || 0);
    const phase = p.phase || 'Single Phase';
    const cashPrice = fmt(p.oriCashPrice);
    return (
        <>
            <div className="program-line">
                TO SUPPLY, INSTALL AND COMMISSION ASTERN AI BESS 360 UNDER {programLine} PROGRAM
            </div>
            <div className="section-title">1.0 SCOPE OF WORK</div>
            <table className="scope-table">
                <thead>
                    <tr><th className="no-col">No</th><th>Item</th><th className="qty-col">Qty</th></tr>
                </thead>
                <tbody>
                    <tr className="avoid-break">
                        <td className="no-col">1</td>
                        <td>
                            <div className="item-title">Supply &amp; Install Solar PV Module with a cumulative capacity of {kwp}kWp</div>
                            <div className="item-desc">- Trina Solar Mono-crystalline half-cell with MyHijau mark or equivalent.</div>
                            <div className="item-desc">- 15 years on product material and workmanship warranty from manufacturer.</div>
                            <div className="item-desc">- 30 years linear output warranty from manufacturer.</div>
                        </td>
                        <td className="qty-col">{panelQty} units<br/>({panelWatt}Wp)</td>
                    </tr>
                    <tr className="avoid-break">
                        <td className="no-col">2</td>
                        <td>
                            <div className="item-title">Supply &amp; Install Hybrid Solar Inverter with a cumulative capacity of {kwac}kWac</div>
                            <div className="item-desc">- Goodwe {phase.toLowerCase()} high efficiency string inverter or equivalent.</div>
                            <div className="item-desc">- 10 years product warranty.</div>
                        </td>
                        <td className="qty-col">1 set</td>
                    </tr>
                    <tr className="avoid-break">
                        <td className="no-col">3</td>
                        <td>
                            <div className="item-title">Supply &amp; Install roof mounting structure</div>
                            <div className="item-desc">- Tile / Metal / Concrete roof mounting structure with rail, clamp and connector.</div>
                            <div className="item-desc">- 10 years product warranty.</div>
                        </td>
                        <td className="qty-col">1 set</td>
                    </tr>
                    <tr className="avoid-break">
                        <td className="no-col">4</td>
                        <td>
                            <div className="item-title">Supply &amp; Install Balance of System/Accessories</div>
                            <div className="item-desc">- Combiner box, breaker, CT Clamp, accessories and all necessary wiring for power and communication.</div>
                            <div className="item-desc">- 2 years product warranty.</div>
                        </td>
                        <td className="qty-col">1 set</td>
                    </tr>
                    <tr className="avoid-break">
                        <td className="no-col">5</td>
                        <td>
                            <div className="item-title">Testing &amp; Commissioning (T&amp;C)</div>
                            <div className="item-desc">- System installation is in accordance to MS1837 of the Malaysian Standard</div>
                        </td>
                        <td className="qty-col">1 job</td>
                    </tr>
                    <tr className="avoid-break">
                        <td className="no-col">6</td>
                        <td>
                            <div className="item-title">Supply Technical Drawings and Authority Submission</div>
                            <div className="item-desc">- Drawings, Technical Design, and related application to SEDA and TNB.</div>
                        </td>
                        <td className="qty-col">1 set</td>
                    </tr>
                    <tr className="avoid-break">
                        <td className="no-col">7</td>
                        <td>
                            <div className="item-title">Operation &amp; Maintenance</div>
                            <div className="item-desc">- Technical support, online monitoring, preventive check, inverter fan cleaning, visual inspection and data log analysis.</div>
                        </td>
                        <td className="qty-col">{p.operationWarrantyYears || '1 year'}</td>
                    </tr>
                    <tr className="avoid-break">
                        <td className="no-col">8</td>
                        <td>
                            <div className="item-title">Defect Liability Period</div>
                            <div className="item-desc">- Labor warranty and roof water-leakage warranty from the date of successful commissioning.</div>
                            <div className="item-desc">- Existing roof issue will void the roof water leaking warranty</div>
                        </td>
                        <td className="qty-col">{p.defectWarrantyYears || '1 year'}</td>
                    </tr>
                    <tr className="total-row">
                        <td colSpan={3}>CASH PRICE: RM {cashPrice}</td>
                    </tr>
                </tbody>
            </table>
        </>
    );
}

function AddOnTable({ p }) {
    const batteryKwh = Number(p.batteryKwh || 0);
    const batteryQty = Number(p.batteryQty || 0);
    const hasBattery = batteryKwh > 0;            // is the row rendered at all?
    const hasActiveBattery = batteryKwh > 0 && batteryQty > 0; // gates backup-box visibility
    const batteryPrice = Number(p.batteryCashPrice || 0);
    const addOnCash = Number(p.addOnPrice || 0);

    // FINAL totals (post-discount, post-add-ons). CC plans = cash final ÷ factor,
    // already tallied upstream — shown as a single clean figure, no discount note.
    const finalCash = Number(p.discountedCashPrice || 0);
    const final36m = Number(p.cc36mPrice || 0);
    const final60m = Number(p.cc60mPrice || 0);

    const rows = [];
    let n = 1;

    if (hasBattery) {
        const battQty = Number(p.batteryQty) || 0;
        const battLineTotal = batteryPrice * battQty;
        rows.push({
            no: n++,
            title: 'Residential Battery Storage System (Backup-Box included)',
            desc: [`- Goodwe BAT ${batteryKwh}kWh`, '- 10 years product and performance warranty'],
            qty: battQty,
            price: battQty > 1
                ? `RM ${fmt(batteryPrice)} × ${battQty} = RM ${fmt(battLineTotal)}`
                : `RM ${fmt(batteryPrice)}`,
        });
    }
    // Render every add-on item the form configured (incl. qty=0 rows so the
    // standard items stay visible on the quotation). Qty=0 rows show "0"
    // explicitly + the unit price as the displayed price; qty>0 rows show
    // the line total. The ADD-ON CASH PRICE total below still sums only
    // qty × price, so it's 0 when everything is qty=0.
    const phase = p.phase || 'Single Phase';
    const items = Array.isArray(p.addOnItems) ? p.addOnItems : [];

    // Backup-box render rules:
    //  - battery qty > 0   -> hide BOTH backup-box rows (bundled with battery)
    //  - battery qty 0 OR no battery -> show ONLY the row matching the phase
    const isBackupRow = (id) => id === 'sp-backup' || id === 'tp-backup';
    const matchesPhase = (id) =>
        (id === 'sp-backup' && phase === 'Single Phase') ||
        (id === 'tp-backup' && phase === 'Three Phase');

    items.filter(it => {
        if (!isBackupRow(it.id)) return true;
        if (hasActiveBattery) return false;
        return matchesPhase(it.id);
    }).forEach(it => {
        const unitPrice = Number(it.price) || 0;
        const qty = Number(it.qty) || 0;
        const lineTotal = unitPrice * qty;
        // Description can be a single string ("Contactor, signal cable, …")
        // or multi-line separated by newlines. Render each non-empty line as
        // its own bullet under the title (matches the old PDF style).
        const descLines = (it.desc || '')
            .split(/\r?\n/)
            .map(s => s.trim())
            .filter(Boolean)
            .map(s => (s.startsWith('-') ? s : `- ${s}`));
        rows.push({
            no: n++,
            title: it.name || '(Unnamed item)',
            desc: descLines,
            qty,
            price: qty > 1
                ? `RM ${fmt(unitPrice)} × ${qty} = RM ${fmt(lineTotal)}`
                : `RM ${fmt(unitPrice)}`,
        });
    });

    return (
        <>
            <div className="section-title">1.1 ADD-ON PRODUCTS AND SERVICES (OPTIONAL)</div>
            <table className="scope-table">
                <thead>
                    <tr><th className="no-col">No</th><th>Item</th><th className="qty-col">Qty</th><th className="price-col">Price</th></tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.no} className="avoid-break">
                            <td className="no-col">{r.no}</td>
                            <td>
                                <div className="item-title">{r.title}</div>
                                {r.desc.map((d, i) => <div key={i} className="item-desc">{d}</div>)}
                            </td>
                            <td className="qty-col">{r.qty}</td>
                            <td className="price-col">{r.price}</td>
                        </tr>
                    ))}
                    <tr className="total-row"><td colSpan={4}>ADD-ON CASH PRICE: RM {fmt(addOnCash)}</td></tr>
                    {/* Final prices only — no strike-through / "(Discount RM …)" note.
                        Each plan already tallies: CC = cash final ÷ factor (round-up
                        RM10), so the customer sees one clean figure per method. */}
                    <tr className="total-row">
                        <td colSpan={4}>FINAL TOTAL CASH PRICE: RM {fmt(finalCash)}</td>
                    </tr>
                    <tr className="total-row">
                        <td colSpan={4}>FINAL 36 MONTHS EASY PAYMENT PLAN: RM {fmt(final36m)}</td>
                    </tr>
                    {final60m > 0 && (
                        <tr className="total-row">
                            <td colSpan={4}>FINAL 60 MONTHS EASY PAYMENT PLAN: RM {fmt(final60m)}</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </>
    );
}

function Notes() {
    return (
        <div className="notes">
            <div className="section-title">2.0 NOTES</div>
            <p>2.1 Quotation does not include any modification to existing electrical network if required by TNB. PV System will be connected directly to house distribution box incoming.</p>
            <p>2.2 Reinforcement of roof to cater for the additional load of the PV panels will be quoted separately if required.</p>
            <p>2.3 Final system size will depend on detail site survey and roof space measurement upon confirmation of order.</p>
            <p>2.4 Product warranty will be void if the parts defects due to third party fault/ vandalism/ act of God/ Grid Error.</p>
            <p>2.5 Hacking and plastering is not included in this quotation. Trunking / PVC will be provided.</p>
            <p>2.6 Booking fee is non-refundable, except when SELCO/SEDA is not approved or roof installation is not possible after site visit.</p>
            <p>2.7 Wifi connection should be provided by the client.</p>
        </div>
    );
}

function PaymentTerm() {
    return (
        <div className="notes">
            <div className="section-title">3.0 PAYMENT TERM</div>
            <p>3.1 Our progressive payment term as below:</p>
            <ol className="payment-list" type="i">
                <li>RM500 upon acceptance and signing of quotation.</li>
                <li>60% upon design and layout confirmation after site visit, before material delivery</li>
                <li>40% upon complete installation, before submission to TNB</li>
            </ol>
            <p className="remark">
                Remark: To ensure smooth project handover and system performance, we kindly request full payment upon installation completion. In cases of delayed payment, after sales services and system operation may be temporarily paused, and further action may be considered if unresolved. We appreciate your support and understanding.
            </p>
        </div>
    );
}

function ProjectDuration() {
    return (
        <div className="notes">
            <div className="section-title">4.0 PROJECT DURATION</div>
            <p>4.1 The project workflow as below:</p>
            <ol className="duration-list" type="i">
                <li>Technical assessment and SEDA/TNB application: 2-4 weeks</li>
                <li>Stock preparation: Ex-stock or 4-6 weeks order</li>
                <li>Installation and configuration: 1-3 days, subject to weather constraints</li>
                <li>Change of TNB bi-directional meter: 2-4 weeks, subject to TNB arrangement (if required)</li>
                <li>Handover and system update: 1 week</li>
            </ol>
        </div>
    );
}

function PreliminaryDesign({ p }) {
    const program = p.program === 'SELCO' || p.program === 'SELCO/ATAP' ? 'SELCO / ATAP' : (p.program || 'ATAP');
    const kwp = Number(p.kwp || 0);
    const panelQty = Number(p.panelQty || 0);
    const panelWatt = p.panelWatt || '640';
    const kwac = Number(p.kwac || 0);
    const phase = p.phase || 'Single Phase';
    // Solar generation estimate: kWp × 3.5 peak sun hours × 365 days
    const yearKwh = Math.round(kwp * 365 * 3.5);
    const monthKwh = Math.round(yearKwh / 12);

    return (
        <>
            <div className="section-title preliminary-title">PRELIMINARY DESIGN</div>
            <table className="design-table">
                <tbody>
                    <tr><td className="label">Solar Application</td><td>{program}</td></tr>
                    <tr><td className="label">TNB Meter Phase</td><td>{phase}</td></tr>
                    <tr><td className="label">System Size</td><td>{kwp.toFixed(2)} kWp / {kwac} kWac</td></tr>
                    <tr><td className="label">Number of panels</td><td>{panelQty} units ({panelWatt}Wp panel)</td></tr>
                    <tr>
                        <td className="label">Solar Generation</td>
                        <td>
                            {yearKwh.toLocaleString()} kWh <span className="muted-italic">(year)</span>
                            <span className="gen-spacer">{monthKwh.toLocaleString()} kWh</span>
                            <span className="muted-italic">(month)</span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div className="notes">
                <p className="bold">Remarks:</p>
                <p className="item">i. Solar Generation is based on average daily 3.5 peak sun hours</p>
                <p className="item">ii. TNB Saving is based on TNB Domestic Tariff but will vary according to usage patterns</p>
                <p className="item">iii. The system may change based on detailed roof assessment and engineering site survey</p>
            </div>
        </>
    );
}

function Signatures({ p }) {
    return (
        <div className="signature-grid">
            <div className="signature-block">
                <div className="caption">Prepared by:</div>
                <div className="line">
                    <div className="bold">ASTERN TECHNOLOGIES SDN. BHD.</div>
                    <div>Name: {p.agentName || ''}</div>
                    <div>Position: {p.agentPosition || ''}</div>
                    <div>Date: {p.date || ''}</div>
                </div>
            </div>
            <div className="signature-block">
                <div className="caption">Accepted by:</div>
                <div className="line">
                    <div>Name: {p.name || ''}</div>
                    <div>Position: Customer</div>
                    <div>Date: {p.date || ''}</div>
                </div>
            </div>
        </div>
    );
}

export default function QuotationPreview() {
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        try {
            setPayload(JSON.parse(raw));
            sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.error('Failed to parse quotation payload', e);
        }
    }, []);

    useEffect(() => {
        if (payload?.name) document.title = `Quotation - ${payload.name}`;
    }, [payload]);

    if (!payload) {
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                <Typography variant="h6">No quotation data found</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#64748B', mt: 1 }}>
                    Open this from the Form Generator's "New Quotation" button.
                </Typography>
            </Box>
        );
    }

    return (
        <Box className="quote-root">
            <Box className="no-print quote-toolbar">
                <Button onClick={() => window.close()} variant="text" size="small">Close</Button>
                <Button onClick={() => window.print()} variant="contained" size="small">
                    Print / Save as PDF
                </Button>
            </Box>
            <QuotationDocument payload={payload} />
        </Box>
    );
}

// Reusable document body (the 3 A4 .quote-page divs) — shared by the preview
// page above AND the Form Generator's direct client-side PDF generation, so the
// quotation can be downloaded without opening a new tab (iPhone-safe).
export function QuotationDocument({ payload }) {
    if (!payload) return null;
    return (
        <>
            <div className="quote-page">
                <Letterhead />
                <div className="quote-title">QUOTATION</div>
                <AttnBlock p={payload} />
                <ScopeOfWork p={payload} />
            </div>

            <div className="quote-page">
                <Letterhead />
                <AddOnTable p={payload} />
                <Notes />
                <PaymentTerm />
                <ProjectDuration />
            </div>

            <div className="quote-page">
                <Letterhead />
                <PreliminaryDesign p={payload} />
                <Signatures p={payload} />
            </div>
        </>
    );
}
